import { useState } from "react";
import PartnerForm from "./PartnerForm";
import { Tag, Space, Button, Row, Col, notification, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, EyeOutlined, AimOutlined } from "@ant-design/icons";
import { useGetPartnersQuery } from "../../redux/services/partnerApi";
import TopScrollTable from "../../components/TopScrollTable";
import AddLocationInPartnerModal from "./AddLocationInPartnerModal";
import { Link } from "react-router-dom";
import Checkbox, { CheckboxChangeEvent } from "antd/es/checkbox";
import PartnerFilters from "./PartnerFilters";

const { Text } = Typography;
interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const Partner = () => {
  const [api, contextHolder] = notification.useNotification();

  const [isOpen, setOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<any>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [queryParams, setQueryParams] = useState<any>({ include_all: true, page: 1, page_size: 20 });

  const { data, isFetching, isLoading, refetch } = useGetPartnersQuery({
    ...queryParams
  });

  const partners = data?.partners || [];

  const onUpdate = (partner: any) => {
    setSelectedPartner(partner);
    setIsEditMode(!isEditMode);
    setOpen(!isOpen);
  };

  const onShowAddLocationModal = (partner: any) => {
    setSelectedPartner(partner);
    setShowModal(true);
  };

  const refetchWithReset = () => {
    const values: any = { page: 1 };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    refetch();
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },

    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => (
        <Text style={{ whiteSpace: "nowrap" }} key={code}>
          {code ? code : "-"}
          <br />
        </Text>
      )
    },
    {
      title: "Contact Person",
      dataIndex: "contact_person",
      key: "contact_person",
      render: (contact_person) => (
        <Text style={{ whiteSpace: "nowrap" }} key={contact_person}>
          {contact_person ? contact_person : "-"}
          <br />
        </Text>
      )
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number",
      render: (contact_number) => (
        <Text style={{ whiteSpace: "nowrap" }} key={contact_number}>
          {contact_number ? contact_number : "-"}
          <br />
        </Text>
      )
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag?.toUpperCase()}
        </Tag>
      )
    },
    // {
    //   title: "Locations",
    //   key: "locations",
    //   dataIndex: "locations",
    //   render: (locations: any[]) => (
    //     <>
    //       {locations.length > 0 &&
    //         locations?.map((loc: any, index: number) => (
    //           <Text key={loc?.location?.name}>
    //             {loc?.location?.name.toUpperCase()} {index === locations?.length - 1 ? "" : "| "}
    //           </Text>
    //         ))}

    //       {locations?.length === 0 && (
    //         <Text style={{ whiteSpace: "nowrap" }}>
    //           -
    //           <br />
    //         </Text>
    //       )}
    //     </>
    //   )
    // },
    {
      title: "Action",
      key: "action",
      render: (_, partner: any) => (
        <Space size="middle">
          <Link to={`${partner?.id}`}>
            <Button icon={<EyeOutlined />} title="View"></Button>
          </Link>
          <Button icon={<EditOutlined />} onClick={() => onUpdate(partner)} title="Update"></Button>
          <Button
            icon={<AimOutlined />}
            onClick={() => onShowAddLocationModal(partner)}
            title="Add Location"
          ></Button>
        </Space>
      )
    }
  ];

  const pagination = {
    pageSize: queryParams?.page_size,
    current: queryParams?.page,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onShowSizeChange: (current: any, page_size: number) => {
      const values: any = { page_size: page_size };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    },
    onChange: (page: number) => {
      const values: any = { page: page };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const onChangeActiveOnly = (e: CheckboxChangeEvent) => {
    const include_all: boolean = e.target.checked;
    const values: any = { include_all: !include_all };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
  };

  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Partners</h2>
            <Text>Total partners: {data?.meta?.total_count || 0}</Text>
          </div>
          <div style={{ display: "flex", gap: 3 }}>
            <Button onClick={() => setOpen(!isOpen)} size="large" type="primary">
              Create Partner
            </Button>
          </div>
        </Col>
      </Row>

      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <Checkbox onChange={onChangeActiveOnly}>Show Active Only</Checkbox>
        </Col>
      </Row>

      <PartnerFilters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
      />

      <TopScrollTable
        rowKey="id"
        columns={columns}
        dataSource={partners}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />

      <AddLocationInPartnerModal
        isModalOpen={showModal}
        partner={selectedPartner}
        onCancel={() => setShowModal(false)}
        refetch={refetchWithReset}
      />

      {isOpen && (
        <PartnerForm
          setOpen={setOpen}
          isOpen={isOpen}
          partnerId={selectedPartner?.id}
          refetch={refetchWithReset}
        />
      )}
    </>
  );
};

export default Partner;
